import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, ReactNode } from "react";
import SVG from "react-inlinesvg";
import IconButton from "src/components/IconButton";
import useMediaQuery from "src/hooks/useMediaQuery";
import { cn } from "src/lib/cn";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "src/sudokit/components/Dialog";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { SudoSegmentedControl } from "src/sudokit/components/SudoSegmentedControl";
import { AnimatePresence, motion } from "framer-motion";
import { Text } from "src/sudokit/components/Text";
import { Textarea } from "src/sudokit/components/Textarea";
import EntityImageAddMoreButton from "./EntityImageAddMoreButton";
import EntityImageMasonryGallery from "./EntityImageMasonryGallery";
import EntityImageMultiUploadDropzone from "./EntityImageMultiUploadDropzone";
import ImageResultGrid from "./ImageResultGrid";
import ImageShimmerOverlay from "./ImageShimmerOverlay";
import LightboxControls from "./LightboxControls";
import ReferenceImageDropzone from "./ReferenceImageDropzone";
import SelectedStylePills from "./SelectedStylePills";
import StylesPopover from "./StylesPopover";
import { RemoveImageMenu, RemoveImageTrigger } from "./TileActionsMenu";
import type { EntityImageListItem, TileState } from "./lib/types";
import { MAX_PENDING_UPLOAD_FILES, MAX_PENDING_UPLOAD_FILES_MESSAGE } from "./lib/constants";
import { orderEntityImages } from "./lib/orderEntityImages";
import { fileFromTile } from "./lib/fileFromImage";
import { getEntityImageAltText } from "./lib/imageAltText";
import { useFadeInImage } from "./lib/useFadeInImage";
import { readFileAsBase64, useImagePayload } from "./lib/useImagePayload";
import { usePendingUploadFiles } from "./lib/usePendingUploadFiles";
import type { UseEntityImageGenerationResult } from "./lib/useEntityImageGeneration";
import { MAX_IMAGE_DESCRIPTION_LENGTH } from "../../../../src/shared/entityImages";

const REFERENCE_THUMBNAIL_PX = 110;

// TODO: re-enable once fetching a saved image's bytes works. The browser fetch of the S3/CDN url fails (CORS), so
// the follow-up is an optional `referenceImageId` on the generate routes that has the server load the image.
const USE_TILE_AS_REFERENCE_ENABLED = false;

export type EntityImageDialogSurface = "create" | "viewer" | "gallery";

export type EntityImageDialogTab = "generate" | "upload";

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

export interface EntityImageDialogCopy {
  /** Label above the description textarea; `usingFreeformStyle` is true when no style presets are selected. */
  descriptionLabel: (usingFreeformStyle: boolean) => string;
  descriptionPlaceholder: (usingFreeformStyle: boolean) => string;
  emptyResultsText: string;
  emptyResultsImageSrc: string;
  emptyResultsImageClassName?: string;
  stylesPopoverWidth?: string;
}

export interface EntityImageDialogAnalytics {
  uploadFilesAdded: (props: { addedCount: number; totalPendingCount: number; rejectedDueToLimit: boolean }) => void;
  uploadSaveClicked: (props: { fileCount: number }) => void;
}

export interface EntityImageGalleryRenderProps {
  images: EntityImageListItem[];
  onSelectImage: (imageId: string) => void;
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}

const TAB_OPTIONS = [
  { value: "generate", label: "Generate" },
  { value: "upload", label: "Upload" },
];

export interface EntityImageDialogProps<TBatchParams = undefined> extends UseEntityImageGenerationResult<TBatchParams> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  surface: EntityImageDialogSurface;
  entityName: string;
  entityRoleLabel?: string | null;
  entityImageUrl?: string | null;
  entityImagePrompt?: string | null;
  /** Shown in the header when the entity has no cover image. */
  fallbackIconSrc: string;
  /** Singular noun for the images, e.g. "portrait" or "image". */
  imageNoun: string;
  copy: EntityImageDialogCopy;
  analytics: EntityImageDialogAnalytics;
  images: EntityImageListItem[];
  imagesLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  initialTab: EntityImageDialogTab;
  initialLightboxImageId?: string | null;
  onDeleteImage: (imageId: string) => Promise<void>;
  onSetCoverImage: (imageId: string) => Promise<void>;
  onAddImage: () => void;
  /** Params applied to every tile in the next generated batch, e.g. an aspect ratio. */
  batchParams?: TBatchParams;
  /** Replaces the default pattern masonry for the "see all" gallery surface. */
  renderGallery?: (props: EntityImageGalleryRenderProps) => ReactNode;
  /** "W:H" ratio for the generated tiles, which take their height from the stage. */
  resultAspectRatio?: string;
}

export default function EntityImageDialog<TBatchParams = undefined>({
  open,
  onOpenChange,
  surface,
  entityName,
  entityRoleLabel,
  entityImageUrl,
  entityImagePrompt,
  fallbackIconSrc,
  imageNoun,
  copy,
  analytics,
  images,
  imagesLoading,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  initialTab,
  initialLightboxImageId,
  onDeleteImage,
  onSetCoverImage,
  onAddImage,
  batchParams,
  renderGallery,
  resultAspectRatio = "1:1",
  isGenerating,
  isSaving,
  itemSaveStatuses,
  description,
  setDescription,
  selectedStyleIds,
  toggleStyle,
  hasGeneratableSelection,
  stylePresets,
  stylePresetsError,
  ensureStylePresetsLoaded,
  setReferenceImageGuidance,
  tiles,
  summarizeError,
  generateError,
  unsavedTileError,
  saveError,
  handleGenerate,
  handleRetryTile,
  saveImportedImages,
}: EntityImageDialogProps<TBatchParams>) {
  const [referenceFile, setReferenceFile] = useState<File | null>(null);
  const [referenceImageError, setReferenceImageError] = useState<string | null>(null);
  const [headerImageFailed, setHeaderImageFailed] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<EntityImageDialogTab>(initialTab);
  const [lightboxImageId, setLightboxImageId] = useState<string | null>(initialLightboxImageId ?? null);
  const [removeMenuOpen, setRemoveMenuOpen] = useState(false);
  const [deletingImageId, setDeletingImageId] = useState<string | null>(null);
  const [settingCoverImageId, setSettingCoverImageId] = useState<string | null>(null);
  const [awaitingNextPage, setAwaitingNextPage] = useState(false);
  const [lightboxInfoOpen, setLightboxInfoOpen] = useState(false);

  const { files: pendingUploadFiles, add: addPendingUploadFiles, remove: removePendingUploadFile, clear: clearPendingUploadFiles } = usePendingUploadFiles();

  // Gates `open` itself (not just CSS) below 875px so Radix doesn't mount an unclosable overlay.
  const canShowDialog = !useMediaQuery("(max-width: 875px)");

  const { previewUrl: referenceImageUrl, payload: referenceImagePayload, error: referenceImageReadError } = useImagePayload(referenceFile);

  // Derived from `images` (not separate state) so it can't go stale after a refetch.
  const coverImageId = images.find((image) => image.status === "COVER")?.id ?? null;
  const savedTileCount = tiles?.filter((tile) => tile.status === "done" && !tile.saveFailed && !!tile.imageId).length ?? 0;

  const isReferenceImageLoading = !!referenceFile && !referenceImagePayload && !referenceImageReadError;
  const stylePresetsLoading = stylePresets === null && !stylePresetsError;
  const canGenerate = !isReferenceImageLoading && !referenceImageReadError && !stylePresetsLoading;
  const showAddMoreButton = pendingUploadFiles.length > 0 && pendingUploadFiles.length < MAX_PENDING_UPLOAD_FILES;
  const referenceImageReadErrorMessage = referenceImageReadError ? "Couldn't read that image, please try a different file." : null;

  const clearReferenceImageState = useCallback(() => {
    setReferenceFile(null);
    setReferenceImageError(null);
    setReferenceImageGuidance("");
  }, [setReferenceImageGuidance]);

  const handleUseTileAsReference = useCallback(async (tile: TileState) => {
    try {
      const file = await fileFromTile(tile);
      setReferenceImageError(null);
      setReferenceFile(file);
    } catch {
      setReferenceImageError("Couldn't use that image as a reference, please try again.");
    }
  }, []);

  const wasSavingRef = useRef(false);
  useEffect(() => {
    if (wasSavingRef.current && !isSaving && !saveError) {
      clearReferenceImageState();
      clearPendingUploadFiles();
    }
    wasSavingRef.current = isSaving;
  }, [isSaving, saveError, clearReferenceImageState, clearPendingUploadFiles]);

  useEffect(() => {
    setHeaderImageFailed(false);
  }, [entityImageUrl]);

  // Fetched as soon as the Generate tab could be shown, so the default preset's label is
  // already resolved by the time Generate could be clicked.
  useEffect(() => {
    if (open && surface === "create") ensureStylePresetsLoaded();
  }, [open, surface, ensureStylePresetsLoaded]);

  const orderedImages = useMemo(() => orderEntityImages(images), [images]);
  const lightboxIndex = lightboxImageId ? orderedImages.findIndex((image) => image.id === lightboxImageId) : -1;
  const lightboxImage = lightboxIndex >= 0 ? orderedImages[lightboxIndex] : null;
  const { loaded: lightboxImageLoaded, ref: attachLightboxImageRef, onLoad: handleLightboxImageLoad } = useFadeInImage(lightboxImage?.url);
  const showLightbox = (surface === "viewer" || surface === "gallery") && lightboxImage != null;
  // Scoped to the current image, not "something is in flight" — the user may have navigated away.
  const isSettingCoverCurrentImage = lightboxImage != null && settingCoverImageId === lightboxImage.id;
  const isDeletingCurrentImage = lightboxImage != null && deletingImageId === lightboxImage.id;
  const isBusyOnCurrentImage = isSettingCoverCurrentImage || isDeletingCurrentImage;
  const canGoPrev = lightboxIndex > 0;
  const canGoNext = lightboxIndex >= 0 && (lightboxIndex < orderedImages.length - 1 || hasNextPage);

  useEffect(() => {
    setRemoveMenuOpen(false);
  }, [lightboxImageId]);

  const prefetchNeighbourImages = useCallback(() => {
    if (!showLightbox || lightboxIndex < 0) return;
    for (const neighbour of [orderedImages[lightboxIndex - 1], orderedImages[lightboxIndex + 1]]) {
      if (neighbour) new Image().src = neighbour.url;
    }
  }, [showLightbox, lightboxIndex, orderedImages]);

  useEffect(prefetchNeighbourImages, [prefetchNeighbourImages]);

  const goToPrevImage = useCallback(() => {
    if (lightboxIndex > 0) setLightboxImageId(orderedImages[lightboxIndex - 1].id);
  }, [lightboxIndex, orderedImages]);

  // Distinguishes "the fetch settled" from "it hasn't started yet" for the effect below, since
  // isFetchingNextPage only flips true on a later render, not synchronously with onLoadMore().
  const hasSeenFetchStartRef = useRef(false);

  const goToNextImage = useCallback(() => {
    if (lightboxIndex < 0) return;
    if (lightboxIndex < orderedImages.length - 1) {
      setLightboxImageId(orderedImages[lightboxIndex + 1].id);
      return;
    }
    if (hasNextPage && !isFetchingNextPage) {
      hasSeenFetchStartRef.current = false;
      setAwaitingNextPage(true);
      onLoadMore();
    }
  }, [lightboxIndex, orderedImages, hasNextPage, isFetchingNextPage, onLoadMore]);

  useEffect(() => {
    if (!awaitingNextPage) return;
    if (isFetchingNextPage) {
      hasSeenFetchStartRef.current = true;
      return;
    }
    if (!hasSeenFetchStartRef.current) return;
    setAwaitingNextPage(false);
    if (lightboxIndex >= 0 && lightboxIndex < orderedImages.length - 1) {
      setLightboxImageId(orderedImages[lightboxIndex + 1].id);
    }
  }, [awaitingNextPage, isFetchingNextPage, lightboxIndex, orderedImages]);

  const wasOpenRef = useRef(open);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setActiveTab(initialTab);
      setLightboxInfoOpen(false);
      // Reseed on every open — the dialog stays mounted between opens, so lightboxImageId
      // otherwise holds a stale value from the previous session.
      const requestedInList = initialLightboxImageId != null && orderedImages.some((image) => image.id === initialLightboxImageId);
      setLightboxImageId(requestedInList ? initialLightboxImageId! : surface === "viewer" ? (orderedImages[0]?.id ?? null) : null);
    }
    wasOpenRef.current = open;
  }, [open, initialTab, initialLightboxImageId, surface, orderedImages]);

  // For a collapsed card the images query only starts once the dialog opens, so the effect
  // above can seed against an empty list — resolve again once the images actually arrive.
  useEffect(() => {
    if (!open || surface !== "viewer" || lightboxImageId != null || orderedImages.length === 0) return;
    const requestedInList = initialLightboxImageId != null && orderedImages.some((image) => image.id === initialLightboxImageId);
    setLightboxImageId(requestedInList ? initialLightboxImageId! : (orderedImages[0]?.id ?? null));
  }, [open, surface, lightboxImageId, initialLightboxImageId, orderedImages]);

  // Closes the dialog if the viewer surface ends up with no images (e.g. the last was deleted).
  const wasOpenForEmptyCheckRef = useRef(open);
  useEffect(() => {
    const isOpeningTransition = open && !wasOpenForEmptyCheckRef.current;
    wasOpenForEmptyCheckRef.current = open;
    // Skip the opening frame: the parent's images query hasn't started yet, so
    // imagesLoading can still read false even though a fetch is about to kick off.
    if (isOpeningTransition) return;
    if (open && surface === "viewer" && !imagesLoading && orderedImages.length === 0) {
      onOpenChange(false);
    }
  }, [open, surface, imagesLoading, orderedImages.length, onOpenChange]);

  const toggleLightboxInfo = useCallback(() => setLightboxInfoOpen((current) => !current), []);

  const closeLightbox = useCallback(() => {
    setLightboxInfoOpen(false);
    if (surface === "viewer") {
      onOpenChange(false);
    } else {
      setLightboxImageId(null);
    }
  }, [surface, onOpenChange]);

  const handleSetCoverImage = useCallback(
    async (imageId: string) => {
      setSettingCoverImageId(imageId);
      try {
        await onSetCoverImage(imageId);
      } catch {
        // Parent already reports the failure (Sentry + toast); nothing further to do here.
      } finally {
        setSettingCoverImageId(null);
      }
    },
    [onSetCoverImage],
  );

  const handleDeleteLightboxImage = useCallback(
    async (imageId: string) => {
      setDeletingImageId(imageId);
      try {
        await onDeleteImage(imageId);
        closeLightbox();
      } catch {
        // Parent already reports the failure (Sentry + toast); nothing further to do here.
      } finally {
        setDeletingImageId(null);
      }
    },
    [onDeleteImage, closeLightbox],
  );

  const handleTabChange = (tab: string) => {
    setActiveTab(tab as EntityImageDialogTab);
  };

  const handleUploadFilesAdded = useCallback(
    (newFiles: File[]) => {
      const remainingSlots = MAX_PENDING_UPLOAD_FILES - pendingUploadFiles.length;
      const addedCount = Math.max(0, Math.min(newFiles.length, remainingSlots));
      analytics.uploadFilesAdded({
        addedCount,
        totalPendingCount: pendingUploadFiles.length + addedCount,
        rejectedDueToLimit: newFiles.length > remainingSlots || remainingSlots <= 0,
      });
      if (remainingSlots <= 0) {
        setUploadError(MAX_PENDING_UPLOAD_FILES_MESSAGE);
        return;
      }
      if (newFiles.length > remainingSlots) setUploadError(MAX_PENDING_UPLOAD_FILES_MESSAGE);
      addPendingUploadFiles(newFiles.slice(0, remainingSlots));
    },
    [pendingUploadFiles.length, addPendingUploadFiles, analytics],
  );

  const handleUploadSave = async () => {
    if (pendingUploadFiles.length === 0) return;
    analytics.uploadSaveClicked({ fileCount: pendingUploadFiles.length });
    setUploadError(null);
    try {
      const items = await Promise.all(pendingUploadFiles.map(async (pending) => ({ id: pending.id, payload: await readFileAsBase64(pending.file) })));
      const result = await saveImportedImages(items);
      // On partial failure, drop only the items that saved so the rest remain for retry.
      result?.succeededIds.forEach(removePendingUploadFile);
    } catch {
      setUploadError("Couldn't read one of those images, please try a different file.");
    }
  };

  const generationErrorMessage = summarizeError || unsavedTileError || generateError || referenceImageError || referenceImageReadErrorMessage;
  const usingFreeformStyle = selectedStyleIds.length === 0;
  const descriptionLabel = copy.descriptionLabel(usingFreeformStyle);
  const descriptionPlaceholder = copy.descriptionPlaceholder(usingFreeformStyle);
  const nounTitle = capitalize(imageNoun);

  return (
    <Dialog
      open={open && canShowDialog}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        className={cn("flex max-w-3xl flex-col overflow-hidden", showLightbox ? "h-[min(85vh,820px)]" : surface === "create" && activeTab === "generate" ? "max-h-[90vh]" : "h-[515px] max-h-[90vh]")}
        backdropColor="always-dark"
        hideClose
        onEscapeKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
          if (surface === "gallery" && lightboxImage) {
            event.preventDefault();
            closeLightbox();
          }
        }}
        onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
          if (!showLightbox) return;
          if (event.key === "ArrowLeft") {
            event.preventDefault();
            goToPrevImage();
          } else if (event.key === "ArrowRight") {
            event.preventDefault();
            goToNextImage();
          }
        }}
      >
        <DialogHeader className="shrink-0 flex-row items-center gap-3 space-y-0 text-left sm:text-left">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-2xl border-2 border-solid border-app-grey-3 bg-app-menu-color shadow-default">
            {entityImageUrl && !headerImageFailed ? (
              <img
                src={entityImageUrl}
                alt={getEntityImageAltText(entityName, entityImagePrompt, imageNoun)}
                className="h-full w-full object-cover"
                decoding="async"
                onError={() => setHeaderImageFailed(true)}
              />
            ) : (
              <SVG
                src={fallbackIconSrc}
                width={32}
                height={32}
                style={{ width: 32, height: 32 }}
                aria-hidden="true"
              />
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col">
            <DialogTitle className="!text-[24px] !text-app-grey-0">{entityName}</DialogTitle>
            {entityRoleLabel && (
              <Text
                size="sm"
                color="grey-1"
              >
                {entityRoleLabel}
              </Text>
            )}
          </div>
          <span className="inline-flex shrink-0 items-center rounded-[3px] bg-app-primary-cta-100 p-1.5 text-[10px] font-semibold uppercase leading-none tracking-wide text-app-primary-cta-high-contrast">Beta</span>
          {showLightbox && lightboxImage && (
            <div className="flex shrink-0 items-center">
              {lightboxImage.status === "COVER" ? (
                <IconButton
                  size={40}
                  iconSize={20}
                  smartFill
                  src="/assets/star.fill.svg"
                  ariaLabel="Current thumbnail"
                  hoverTipText="Current thumbnail"
                  className="pointer-events-none"
                />
              ) : (
                <IconButton
                  size={40}
                  iconSize={20}
                  smartFill
                  src="/assets/star.svg"
                  ariaLabel="Set as thumbnail"
                  hoverTipText="Set as thumbnail"
                  disabled={isBusyOnCurrentImage}
                  onClick={() => void handleSetCoverImage(lightboxImage.id)}
                />
              )}
              <RemoveImageMenu
                trigger={
                  <RemoveImageTrigger
                    tooltipDisabled={removeMenuOpen}
                    disabled={isBusyOnCurrentImage}
                  />
                }
                removing={isDeletingCurrentImage}
                onRemove={() => void handleDeleteLightboxImage(lightboxImage.id)}
                onOpenChange={setRemoveMenuOpen}
              />
              <IconButton
                size={40}
                iconSize={20}
                smartFill
                src="/assets/close.svg"
                ariaLabel={surface === "viewer" ? "Close" : "Back to gallery"}
                onClick={closeLightbox}
              />
            </div>
          )}
          {surface === "create" && (
            <SudoSegmentedControl
              options={TAB_OPTIONS}
              value={activeTab}
              onChange={handleTabChange}
              aria-label={`${nounTitle} dialog view`}
            />
          )}
          {surface === "gallery" && !showLightbox && (
            <SudoButton
              type="button"
              variant="secondary"
              size="small"
              leftIcon="/assets/add.svg"
              onClick={onAddImage}
            >
              Add {imageNoun}
            </SudoButton>
          )}
          <DialogDescription className="sr-only">
            Manage {imageNoun}s for {entityName}.
          </DialogDescription>
        </DialogHeader>

        {showLightbox && lightboxImage ? (
          <div className="group relative mt-6 flex min-h-0 w-full flex-1 items-center justify-center overflow-hidden rounded-2xl bg-[#111]">
            <img
              ref={attachLightboxImageRef}
              src={lightboxImage.url}
              alt={getEntityImageAltText(entityName, lightboxImage.prompt, imageNoun)}
              decoding="async"
              className={cn("h-full w-full object-contain transition-opacity duration-300", lightboxImageLoaded ? "opacity-100" : "opacity-0")}
              onLoad={handleLightboxImageLoad}
            />
            {isBusyOnCurrentImage && <ImageShimmerOverlay />}
            <LightboxControls
              image={lightboxImage}
              imageNoun={imageNoun}
              canGoPrev={canGoPrev}
              canGoNext={canGoNext}
              awaitingNextPage={awaitingNextPage}
              infoOpen={lightboxInfoOpen}
              onToggleInfo={toggleLightboxInfo}
              onPrev={goToPrevImage}
              onNext={goToNextImage}
            />
          </div>
        ) : surface === "gallery" ? (
          renderGallery ? (
            renderGallery({ images, onSelectImage: setLightboxImageId, isLoading: imagesLoading, hasNextPage, isFetchingNextPage, onLoadMore })
          ) : (
            <EntityImageMasonryGallery
              entityName={entityName}
              images={images}
              onSelectImage={setLightboxImageId}
              isLoading={imagesLoading}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              onLoadMore={onLoadMore}
              onRemoveImage={onDeleteImage}
            />
          )
        ) : surface === "viewer" ? null : activeTab === "generate" ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div
              className={cn(
                "relative mt-6 flex shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-solid border-app-grey-3 p-4 transition-[padding] duration-300 ease-out",
                tiles === null && "bg-app-grey-4",
                savedTileCount > 0 && "pb-11",
              )}
            >
              {/* Fixed stage so a 2x2 grid cell stays 175px; the container grows around it for the saved label. */}
              <div className="flex h-[360px] w-full items-center justify-center">
                {tiles ? (
                  <ImageResultGrid
                    tiles={tiles}
                    coverImageId={coverImageId}
                    settingCoverImageId={settingCoverImageId}
                    canGenerate={canGenerate}
                    onRetry={handleRetryTile}
                    onSetThumbnail={(imageId) => void handleSetCoverImage(imageId)}
                    onUseReference={USE_TILE_AS_REFERENCE_ENABLED ? (tile) => void handleUseTileAsReference(tile) : undefined}
                    aspectRatio={resultAspectRatio}
                    imageNoun={imageNoun}
                  />
                ) : (
                  <div className="flex flex-col items-center gap-4">
                    <img
                      src={copy.emptyResultsImageSrc}
                      alt=""
                      className={copy.emptyResultsImageClassName ?? "w-[200px]"}
                      aria-hidden="true"
                    />
                    <Text
                      size="sm"
                      color="grey-1"
                      align="center"
                      className="max-w-[220px]"
                      weight="medium"
                    >
                      {copy.emptyResultsText}
                    </Text>
                  </div>
                )}
              </div>
              <AnimatePresence>
                {savedTileCount > 0 && (
                  <motion.div
                    key="saved-to-gallery"
                    initial={{ y: "100%", x: "-50%", opacity: 0 }}
                    animate={{ y: 0, x: "-50%", opacity: 1 }}
                    exit={{ y: "100%", x: "-50%", opacity: 0 }}
                    transition={{ duration: 0.35, ease: [0.22, 0.9, 0.32, 1] }}
                    className="pointer-events-none absolute bottom-0 left-1/2 flex h-[28px] items-center gap-1 rounded-b-none rounded-t-[10px] bg-app-grey-4 px-2 text-xs font-normal leading-none text-app-grey-0"
                  >
                    <SVG
                      src="/assets/check-tick.svg"
                      width={14}
                      height={14}
                      className="shrink-0 [&_path]:fill-current"
                      aria-hidden="true"
                    />
                    <span className="whitespace-nowrap">Saved to gallery</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="mt-3 flex shrink-0 items-end gap-3 rounded-xl border border-solid border-app-grey-3 p-2.5 transition-colors duration-100 focus-within:border-app-primary-cta-high-contrast">
              <ReferenceImageDropzone
                file={referenceFile}
                previewUrl={referenceImageUrl}
                onSelect={(file) => {
                  setReferenceImageError(null);
                  setReferenceFile(file);
                }}
                onError={setReferenceImageError}
                onRemove={clearReferenceImageState}
                size={REFERENCE_THUMBNAIL_PX}
              />
              <div
                className="flex min-w-0 flex-1 flex-col justify-between gap-2"
                style={{ minHeight: REFERENCE_THUMBNAIL_PX }}
              >
                <Textarea
                  short
                  aria-label={descriptionLabel}
                  // Triple-& outranks the shared Textarea's own border/shadow/overflow classes, which tie with plain utilities since all are !important. The composer's border shows focus instead.
                  className="[&&&]:overflow-y-auto [&&&]:border-0 [&&&]:p-0 [&&&]:shadow-none [&&&]:outline-none"
                  value={description}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                  maxLength={MAX_IMAGE_DESCRIPTION_LENGTH}
                  placeholder={descriptionPlaceholder}
                  minRows={3}
                  maxRows={4}
                />
                <div className="flex items-end gap-2">
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
                    <StylesPopover
                      stylePresets={stylePresets}
                      stylePresetsError={stylePresetsError}
                      onRetry={ensureStylePresetsLoaded}
                      selectedStyleIds={selectedStyleIds}
                      onToggleStyle={toggleStyle}
                      width={copy.stylesPopoverWidth}
                      disabled={isGenerating}
                    />
                    <SelectedStylePills
                      stylePresets={stylePresets}
                      selectedStyleIds={selectedStyleIds}
                      onRemoveStyle={toggleStyle}
                      disabled={isGenerating}
                    />
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    {description.length >= MAX_IMAGE_DESCRIPTION_LENGTH * 0.9 && (
                      <Text
                        size="sm"
                        className={cn("text-xs text-app-grey-1", description.length >= MAX_IMAGE_DESCRIPTION_LENGTH && "text-app-danger-color")}
                      >
                        {description.length}/{MAX_IMAGE_DESCRIPTION_LENGTH}
                      </Text>
                    )}
                    <SudoButton
                      type="button"
                      variant="primary"
                      size="small"
                      leftIcon="/assets/AI-sparkle.svg"
                      disabled={isGenerating || isSaving || !canGenerate || !hasGeneratableSelection || !!unsavedTileError}
                      onClick={() => void handleGenerate(referenceImagePayload, batchParams)}
                    >
                      {isGenerating ? "Generating…" : "Generate"}
                    </SudoButton>
                  </div>
                </div>
              </div>
            </div>

            {generationErrorMessage && (
              <DialogFooter className="shrink-0 flex-col items-start justify-start sm:flex-col sm:items-start sm:justify-start">
                <div className="rounded-xl border border-[color-mix(in_srgb,var(--danger-color)_30%,transparent)] bg-[color-mix(in_srgb,var(--danger-color)_5%,transparent)] px-4 py-3 text-left text-sm text-app-danger-color">
                  {generationErrorMessage}
                </div>
              </DialogFooter>
            )}
          </div>
        ) : (
          <>
            <div className="mt-6 flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
              {(saveError || uploadError) && (
                <div className="box-border flex w-full items-center justify-between gap-3 rounded-xl border border-[color-mix(in_srgb,var(--danger-color)_30%,transparent)] bg-[color-mix(in_srgb,var(--danger-color)_5%,transparent)] px-4 py-3 text-left text-sm text-app-danger-color">
                  <span>{saveError || uploadError}</span>
                  {uploadError && (
                    <IconButton
                      size={24}
                      iconSize={12}
                      smartFill
                      ariaLabel="Dismiss error"
                      className="!text-app-danger-color opacity-70 transition-opacity hover:opacity-100"
                      src="/assets/close.svg"
                      onClick={() => setUploadError(null)}
                    />
                  )}
                </div>
              )}
              <div className="min-h-0 flex-1">
                <EntityImageMultiUploadDropzone
                  files={pendingUploadFiles}
                  itemSaveStatuses={itemSaveStatuses}
                  onFilesAdded={handleUploadFilesAdded}
                  onRemove={removePendingUploadFile}
                  onError={setUploadError}
                />
              </div>
            </div>

            {pendingUploadFiles.length > 0 && (
              <DialogFooter className="shrink-0 flex-row items-center justify-between sm:flex-row sm:items-center sm:justify-between">
                {showAddMoreButton && (
                  <EntityImageAddMoreButton
                    onFilesAdded={handleUploadFilesAdded}
                    onError={setUploadError}
                  />
                )}
                <SudoButton
                  type="button"
                  variant="primary"
                  leftIcon="/assets/check-tick.svg"
                  state={isSaving ? "loading" : undefined}
                  disabled={isSaving || isGenerating || pendingUploadFiles.length === 0}
                  onClick={() => void handleUploadSave()}
                  className="ml-auto"
                >
                  Save
                </SudoButton>
              </DialogFooter>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
